<script>
  // The Default Want List's Add a card input (spec "Wants" > Default Want
  // List only): the first row of the table, or the first tile in card view
  // (`compact`). As you type it suggests card names from the server
  // (search-api.suggestCardNames): names containing the text, most-played
  // first, or, only when none does, the closest spellings. Picking one
  // (click, or Up/Down then Enter) adds it. Enter before the suggestions are
  // in waits for them and adds the first; with none at all it adds the typed
  // text (the server canonicalizes it). On success the input clears
  // for the next card; on failure the text stays and the row says so.
  import { tick } from 'svelte';
  import { suggestCardNames } from '../../lib/services/search-api.js';
  import { portal } from '../../lib/actions/portal.js';
  import { comboboxKey, activeDescendant } from '../../lib/logic/combobox-keys.js';
  import { ADD_CARD_PAUSE_MS } from '../../lib/logic/config.js';

  /**
   * @type {{
   *   onAdd: (name: string) => Promise<boolean>,
   *   compact?: boolean,
   * }}
   */
  let { onAdd, compact = false } = $props();

  let q = $state('');
  let suggestions = $state(/** @type {string[]} */ ([]));
  let closest = $state(false);
  let active = $state(0);
  let open = $state(false);
  let adding = $state(false);
  let failed = $state('');
  let input = $state(/** @type {HTMLInputElement | null} */ (null));
  let popStyle = $state('');
  // The latest lookup; an older answer landing late is dropped.
  let seq = 0;
  // A lookup waiting on the typing pause (0: none), and the one in flight.
  let timer = 0;
  let inflight = /** @type {Promise<void> | null} */ (null);
  const listId = `add-card-${Math.random().toString(36).slice(2, 8)}`;

  /** The popup sits under the input, in <body> (the table clips). */
  function place() {
    if (!input) return;
    const r = input.getBoundingClientRect();
    const width = Math.max(r.width, 260);
    const left = Math.max(8, Math.min(r.left, document.documentElement.clientWidth - width - 8));
    popStyle = `left:${left}px;top:${r.bottom + 4}px;width:${width}px`;
  }

  function close() {
    open = false;
  }

  /**
   * Ask for the suggestions for `text`; resolves once they are shown (or
   * dropped, when newer text has been asked about since).
   * @param {string} text
   * @returns {Promise<void>}
   */
  function lookup(text) {
    const mine = ++seq;
    const t = text.trim();
    if (!t) {
      suggestions = [];
      close();
      inflight = null;
      return Promise.resolve();
    }
    const run = suggestCardNames(t).then((res) => {
      if (mine !== seq) return;
      inflight = null;
      suggestions = res.names;
      closest = res.closest;
      active = 0;
      place();
      open = suggestions.length > 0;
    });
    inflight = run;
    return run;
  }

  // A typing pause (ADD_CARD_PAUSE_MS) before asking, so a word typed fast is
  // one request. What is too short to suggest for is the server's call.
  function onInput() {
    failed = '';
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = 0;
      lookup(q);
    }, ADD_CARD_PAUSE_MS);
  }

  /**
   * The suggestions for the text as it stands: a lookup still waiting on
   * the pause runs now, one in flight is awaited.
   * @returns {Promise<void> | null}
   */
  function settleLookup() {
    if (timer) {
      clearTimeout(timer);
      timer = 0;
      return lookup(q);
    }
    return inflight;
  }

  /** Enter: the highlighted suggestion, once the text's suggestions are in;
   *  the typed text only when there are none. */
  async function pick() {
    const waiting = settleLookup();
    if (waiting) await waiting;
    add(open && suggestions[active] ? suggestions[active] : q);
  }

  /** @param {string} name */
  async function add(name) {
    const card = name.trim();
    if (!card || adding) return;
    clearTimeout(timer);
    timer = 0;
    seq += 1; // nothing still in flight reopens the list
    inflight = null;
    close();
    adding = true;
    const ok = await onAdd(card);
    adding = false;
    if (ok) {
      q = '';
      suggestions = [];
      failed = '';
    } else {
      failed = card;
    }
    await tick(); // the input is enabled again before it takes focus
    input?.focus();
  }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    const hit = comboboxKey(e.key, { open, active, count: suggestions.length });
    if (!hit) return;
    e.preventDefault();
    if (hit.stop) e.stopPropagation();
    const { action } = hit;
    if (action.type === 'open') open = true;
    else if (action.type === 'move') active = action.active;
    else if (action.type === 'pick') pick();
    else close();
  }
</script>

<svelte:window onresize={() => open && place()} onscrollcapture={() => open && place()} />

<div class="add" class:compact>
  <input
    bind:this={input}
    bind:value={q}
    oninput={onInput}
    onkeydown={onKeydown}
    onfocus={() => {
      if (suggestions.length) {
        place();
        open = true;
      }
    }}
    onblur={close}
    placeholder="Add a card"
    aria-label="Add a card"
    role="combobox"
    aria-expanded={open}
    aria-controls={listId}
    aria-autocomplete="list"
    aria-activedescendant={activeDescendant(listId, open, active)}
    autocomplete="off"
    disabled={adding}
  />
  {#if failed}<span class="failed" role="alert">Couldn't add {failed}</span>{/if}
</div>

{#if open && suggestions.length}
  <ul
    id={listId}
    class="suggest"
    role="listbox"
    aria-label="Card names"
    style={popStyle}
    use:portal
  >
    {#each suggestions as s, i (s)}
      <!-- Keyboard picks through the input (combobox); the list never takes focus. -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <li
        id="{listId}-{i}"
        class="opt"
        class:active={i === active}
        role="option"
        aria-selected={i === active}
        onpointerdown={(e) => e.preventDefault()}
        onclick={() => add(s)}
        onpointerenter={() => (active = i)}
      >
        {s}
      </li>
    {/each}
    {#if closest}<li class="note" role="presentation">Closest spellings</li>{/if}
  </ul>
{/if}

<style>
  .add {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  input {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 12px;
    border: 1.5px dashed var(--color-border);
    border-radius: 10px;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: var(--color-text);
  }
  input:focus {
    outline: none;
    border-style: solid;
    border-color: var(--color-accent-dark);
    background: var(--color-bg-surface);
  }
  /* In the add tile the input is the tile's label, like "New project". */
  .compact {
    width: 100%;
    flex-direction: column;
    gap: 4px;
  }
  .compact input,
  .compact input:focus {
    width: 100%;
    height: 30px;
    padding: 0 4px;
    border: none;
    background: transparent;
    text-align: center;
    font-weight: 700;
  }
  .compact input::placeholder {
    color: var(--color-text-secondary);
  }
  .failed {
    font-size: 12px;
    color: var(--color-error);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .suggest {
    position: fixed;
    z-index: 2000;
    margin: 0;
    padding: 4px;
    list-style: none;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-bg-surface);
    box-shadow: 0 10px 30px rgba(44, 36, 25, 0.2);
  }
  .opt {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text);
    cursor: pointer;
  }
  .opt.active {
    background: var(--color-accent-bg);
  }
  .note {
    padding: 6px 10px 4px;
    font-size: 11px;
    color: var(--color-text-tertiary);
  }
</style>
